import React, { useContext, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { Card } from "../common/Card";
import { ZahtjevContext } from "../../common/context/ZahtjevProvider";
import { Button } from "../common/Button";
import { DELIVERY_ADDRESS } from "../../model/ContactAddressType";
import { ERROR_MESSAGE_NOTIFICATION } from "../../common/constants/notificationConstants";
import { useFinishRegistration } from "../../api/RegistrationApi";
import { useNotification } from "../../common/context/NotificationProvider";
import { SUMMARY_STATUS } from "../../common/constants/navigationConstants";
import { DOCUMENT_TYPE_RADIO_GROUP_OPTIONS, GENDER_RADIO_GROUP_OPTIONS, SAD_OZNAKA } from "../../common/data/sifarnik";
import { Box, FormControl, InputLabel } from "@mui/material";
import SummaryPageView from "./SummaryPageView";
import { useTranslation } from "react-i18next";
import { getCountryById, getUkrajinaRegijaById } from "../../util/sifarnikUtil";
import { SupportedLanguage } from "../../i18n";
import { RadioGroupOption } from "../common/RadioGroupView";

const SummaryPage = () => {
    const { t, i18n } = useTranslation();
    const lng = i18n.resolvedLanguage as SupportedLanguage;
    const navigate = useNavigate();
    const {
        oibInformation,
        personalInformation,
        contactAddressInformation,
        financialInformation,
        taxResidency,
        politicalExposure,
        marketingConsent,
        restoreContext,
        getStatus,
        getZahtjevId,
        setIsProcessCompleted,
        clearContext,
    } = useContext(ZahtjevContext);
    const { isPending: isLoading, mutate: finishRegistration, } = useFinishRegistration(getZahtjevId());
    const currentStatus = getStatus();
    const pageStatus = SUMMARY_STATUS;
    const { notify } = useNotification();

    const yesNo = (value?: boolean) => t(value ? "common.yes" : "common.no");

    // The FATCA "additional US citizenship" toggle is only shown when neither
    // citizenship is US, so only surface its answer here when it was asked.
    const hasUsCitizenship =
      personalInformation?.drzavljanstvo?.oznaka === SAD_OZNAKA ||
      (!!personalInformation?.imaDrugoDrzavljanstvo &&
        personalInformation?.drzavljanstvoDrugo?.oznaka === SAD_OZNAKA);

    const radioLabel = (options: RadioGroupOption[], value: string | undefined | null) => {
        const option = options.find(o => o.value === value);
        return option ? t(option.label) : "";
    };

    const phoneNumber = (predbroj?: string, broj?: string) => {
        if (!broj) return '';
        return predbroj ? `+${predbroj} ${broj}` : broj;
    };

    const handleSubmit = () => {
        const language = lng?.toUpperCase() as "HR" | "EN";
        finishRegistration({ language }, {
            onSuccess: () => {
                clearContext();
                setIsProcessCompleted(true);
                navigate("/completed");
            },
            onError: () => {
                notify(ERROR_MESSAGE_NOTIFICATION, "error");
            }
        });
    };


    const handleReturn = () => {
        navigate('/marketing-consent', { state: { isBack: true } });
    }

    useEffect(() => {
        restoreContext(pageStatus);
    }, []);

    return (
        <Card title={t("summary-page.title")} isLoading={parseInt(currentStatus) < parseInt(pageStatus)}>
            <Card.Body title={t("summary-page.personal-information.title")} bodyClassName="!space-y-0">
                <SummaryPageView
                    label={t("summary-page.personal-information.views.oib")}
                    value={oibInformation.oib}
                />

                <SummaryPageView
                    label={t("summary-page.personal-information.views.id-document-type")}
                    value={radioLabel(DOCUMENT_TYPE_RADIO_GROUP_OPTIONS, personalInformation.idDokumentVrsta)}
                />

                <SummaryPageView
                    label={t("summary-page.personal-information.views.id-document-number")}
                    value={personalInformation.idDokumentBroj}
                />

                <SummaryPageView
                    label={t("summary-page.personal-information.views.id-document-issuer")}
                    value={personalInformation.idDokumentIzdavatelj}
                />

                <SummaryPageView
                    label={t("summary-page.personal-information.views.id-document-issuer-country")}
                    value={getCountryById(personalInformation.idDokumentDrzavaIzdavanja?.oznaka, lng).drzava || ''}
                />

                {personalInformation.idDokumentDrzavaIzdavanjaRegija?.oznaka && (
                    <SummaryPageView
                        label={t("common.ukraine-region.summary-label")}
                        value={getUkrajinaRegijaById(personalInformation.idDokumentDrzavaIzdavanjaRegija.oznaka, t).drzava || ''}
                    />
                )}

                {!personalInformation.idDokumentTrajnoVazenje && (
                    <SummaryPageView
                        label={t("summary-page.personal-information.views.id-document-expiration-date")}
                        value={personalInformation.idDokumentDatumVazenja}
                    />
                )}

                {personalInformation.idDokumentVrsta === 'osobna' && !personalInformation.idDokumentDatumVazenja && (
                    <SummaryPageView
                        label={t("summary-page.personal-information.views.id-document-permanent-expiration-date")}
                        value={yesNo(personalInformation.idDokumentTrajnoVazenje)}
                    />
                )}

                <SummaryPageView
                    label={t("summary-page.personal-information.views.gender")}
                    value={radioLabel(GENDER_RADIO_GROUP_OPTIONS, personalInformation.spol)}
                />

                <SummaryPageView
                    label={t("summary-page.personal-information.views.name")}
                    value={personalInformation.ime}
                />

                <SummaryPageView
                    label={t("summary-page.personal-information.views.surname")}
                    value={personalInformation.prezime}
                />

                <SummaryPageView
                    label={t("summary-page.personal-information.views.date-of-birth")}
                    value={personalInformation.datumRodjenja}
                />

                <SummaryPageView
                    label={t("summary-page.personal-information.views.place-of-birth")}
                    value={personalInformation.mjestoRodjenja}
                />

                <SummaryPageView
                    label={t("summary-page.personal-information.views.country-of-birth")}
                    value={getCountryById(personalInformation.drzavaRodjenja?.oznaka, lng).drzava || ''}
                />

                {personalInformation.drzavaRodjenjaRegija?.oznaka && (
                    <SummaryPageView
                        label={t("common.ukraine-region.summary-label")}
                        value={getUkrajinaRegijaById(personalInformation.drzavaRodjenjaRegija.oznaka, t).drzava || ''}
                    />
                )}

                <SummaryPageView
                    label={t("summary-page.personal-information.views.citizenship")}
                    value={getCountryById(personalInformation.drzavljanstvo?.oznaka, lng).drzava || ''}
                />

                {personalInformation.drzavljanstvoRegija?.oznaka && (
                    <SummaryPageView
                        label={t("common.ukraine-region.summary-label")}
                        value={getUkrajinaRegijaById(personalInformation.drzavljanstvoRegija.oznaka, t).drzava || ''}
                    />
                )}

                <SummaryPageView
                    label={t("summary-page.personal-information.views.dual-citizenship")}
                    value={yesNo(personalInformation.imaDrugoDrzavljanstvo)}
                />

                {personalInformation.imaDrugoDrzavljanstvo && personalInformation.drzavljanstvoDrugo && (
                    <SummaryPageView
                        label={t("summary-page.personal-information.views.second-citizenship")}
                        value={getCountryById(personalInformation.drzavljanstvoDrugo.oznaka, lng).drzava || ''}
                    />
                )}

                {personalInformation.imaDrugoDrzavljanstvo && personalInformation.drzavljanstvoDrugoRegija?.oznaka && (
                    <SummaryPageView
                        label={t("common.ukraine-region.summary-label")}
                        value={getUkrajinaRegijaById(personalInformation.drzavljanstvoDrugoRegija.oznaka, t).drzava || ''}
                    />
                )}

                <div className="text-base font-medium text-gray-700 pt-6 pb-1">
                    {t("summary-page.personal-information.views.contact-information.info")}
                </div>

                <SummaryPageView
                    label={t("summary-page.personal-information.views.contact-information.mobile-number")}
                    value={phoneNumber(contactAddressInformation.mobitelPredbroj, contactAddressInformation.mobitel)}
                />

                {contactAddressInformation.telefonPredbroj && contactAddressInformation.telefon && (
                    <SummaryPageView
                        label={t("summary-page.personal-information.views.contact-information.telephone-number")}
                        value={phoneNumber(contactAddressInformation.telefonPredbroj, contactAddressInformation.telefon)}
                    />
                )}

                <SummaryPageView
                    label={t("summary-page.personal-information.views.contact-information.email")}
                    value={contactAddressInformation.mailAdresa}
                />

                <SummaryPageView
                    label={t("summary-page.personal-information.views.contact-information.street")}
                    value={contactAddressInformation.ulica}
                />

                <SummaryPageView
                    label={t("summary-page.personal-information.views.contact-information.street-number")}
                    value={contactAddressInformation.kucniBr}
                />

                <SummaryPageView
                    label={t("summary-page.personal-information.views.contact-information.location")}
                    value={contactAddressInformation.mjesto}
                />

                <SummaryPageView
                    label={t("summary-page.personal-information.views.contact-information.zip-code")}
                    value={contactAddressInformation.postanskiBroj}
                />

                <SummaryPageView
                    label={t("summary-page.personal-information.views.contact-information.country")}
                    value={getCountryById(contactAddressInformation.drzava?.oznaka, lng).drzava || ''}
                />

                {contactAddressInformation.drzavaRegija?.oznaka && (
                    <SummaryPageView
                        label={t("common.ukraine-region.summary-label")}
                        value={getUkrajinaRegijaById(contactAddressInformation.drzavaRegija.oznaka, t).drzava || ''}
                    />
                )}

                <SummaryPageView
                    label={t("summary-page.personal-information.residence-address.different-from-residence")}
                    value={yesNo(contactAddressInformation.boraviste)}
                />

                {contactAddressInformation.boraviste && (
                    <>
                        <SummaryPageView
                            label={t("summary-page.personal-information.residence-address.address-field")}
                            value={contactAddressInformation.boravisteUlica}
                        />

                        <SummaryPageView
                            label={t("summary-page.personal-information.residence-address.house-number-field")}
                            value={contactAddressInformation.boravisteKucniBr}
                        />

                        <SummaryPageView
                            label={t("summary-page.personal-information.residence-address.location-field")}
                            value={contactAddressInformation.boravisteMjesto}
                        />

                        <SummaryPageView
                            label={t("summary-page.personal-information.residence-address.zip-code-field")}
                            value={contactAddressInformation.boravistePostanskiBroj}
                        />

                        <SummaryPageView
                            label={t("summary-page.personal-information.residence-address.country-field")}
                            value={getCountryById(contactAddressInformation.boravisteDrzava?.oznaka, lng).drzava || ''}
                        />

                        {contactAddressInformation.boravisteDrzavaRegija?.oznaka && (
                            <SummaryPageView
                                label={t("common.ukraine-region.summary-label")}
                                value={getUkrajinaRegijaById(contactAddressInformation.boravisteDrzavaRegija.oznaka, t).drzava || ''}
                            />
                        )}

                        <SummaryPageView
                            label={t("contact-address-page.delivery-documentation-checkbox")}
                            value={yesNo(contactAddressInformation.adresaZaDostavuDokumentacije === DELIVERY_ADDRESS.BORAVISTE)}
                        />
                    </>
                )}

                <SummaryPageView
                    label={t("summary-page.personal-information.views.contact-information.delivery-address")}
                    value={yesNo(contactAddressInformation.dostavaDrugaAdresa)}
                />

                {contactAddressInformation.dostavaDrugaAdresa && (
                    <>
                        <SummaryPageView
                            label={t("summary-page.personal-information.views.contact-information.street")}
                            value={contactAddressInformation.dostavaUlica}
                        />

                        <SummaryPageView
                            label={t("summary-page.personal-information.views.contact-information.street-number")}
                            value={contactAddressInformation.dostavaKucniBr}
                        />

                        <SummaryPageView
                            label={t("summary-page.personal-information.views.contact-information.location")}
                            value={contactAddressInformation.dostavaMjesto}
                        />

                        <SummaryPageView
                            label={t("summary-page.personal-information.views.contact-information.zip-code")}
                            value={contactAddressInformation.dostavaPostanskiBroj}
                        />

                        <SummaryPageView
                            label={t("summary-page.personal-information.views.contact-information.country")}
                            value={getCountryById(contactAddressInformation.dostavaDrzava?.oznaka, lng).drzava || ''}
                        />

                        {contactAddressInformation.dostavaDrzavaRegija?.oznaka && (
                            <SummaryPageView
                                label={t("common.ukraine-region.summary-label")}
                                value={getUkrajinaRegijaById(contactAddressInformation.dostavaDrzavaRegija.oznaka, t).drzava || ''}
                            />
                        )}

                        <SummaryPageView
                            label={t("contact-address-page.delivery-documentation-checkbox")}
                            value={yesNo(contactAddressInformation.adresaZaDostavuDokumentacije === DELIVERY_ADDRESS.DRUGA)}
                        />
                    </>
                )}

                {contactAddressInformation.trecaAdresa && (
                    <>
                        <div className="text-base font-medium text-gray-700 pt-6 pb-1">
                            {t("summary-page.personal-information.views.contact-information.third-address")}
                        </div>

                        <SummaryPageView
                            label={t("summary-page.personal-information.views.contact-information.street")}
                            value={contactAddressInformation.trecaUlica}
                        />

                        <SummaryPageView
                            label={t("summary-page.personal-information.views.contact-information.street-number")}
                            value={contactAddressInformation.trecaKucniBr}
                        />

                        <SummaryPageView
                            label={t("summary-page.personal-information.views.contact-information.location")}
                            value={contactAddressInformation.trecaMjesto}
                        />

                        <SummaryPageView
                            label={t("summary-page.personal-information.views.contact-information.zip-code")}
                            value={contactAddressInformation.trecaPostanskiBroj}
                        />

                        <SummaryPageView
                            label={t("summary-page.personal-information.views.contact-information.country")}
                            value={getCountryById(contactAddressInformation.trecaDrzava?.oznaka, lng).drzava || ''}
                        />

                        {contactAddressInformation.trecaDrzavaRegija?.oznaka && (
                            <SummaryPageView
                                label={t("common.ukraine-region.summary-label")}
                                value={getUkrajinaRegijaById(contactAddressInformation.trecaDrzavaRegija.oznaka, t).drzava || ''}
                            />
                        )}

                        <SummaryPageView
                            label={t("contact-address-page.delivery-documentation-checkbox")}
                            value={yesNo(contactAddressInformation.adresaZaDostavuDokumentacije === DELIVERY_ADDRESS.TRECA)}
                        />
                    </>
                )}
            </Card.Body>

            <Card.Body title={t("summary-page.financial-information.title")} bodyClassName="!space-y-0">
                {financialInformation?.statusZaposlenja?.vrijednost && (
                    <SummaryPageView
                        label={t("summary-page.financial-information.views.employment-status")}
                        value={t(financialInformation.statusZaposlenja.vrijednost)}
                    />
                )}

                {financialInformation?.vrstaPoslodavca?.vrijednost && (
                    <SummaryPageView
                        label={t("summary-page.financial-information.views.employer-type")}
                        value={t(financialInformation.vrstaPoslodavca.vrijednost)}
                    />
                )}

                {financialInformation?.prosjecnaMjesecnaPrimanjaRedovna?.vrijednost && (
                    <SummaryPageView
                        label={t("summary-page.financial-information.views.average-monthly-income")}
                        value={t(financialInformation.prosjecnaMjesecnaPrimanjaRedovna.vrijednost)}
                    />
                )}

                {financialInformation?.prosjecanIznosOstalihPrihoda?.vrijednost && (
                    <SummaryPageView
                        label={t("summary-page.financial-information.views.average-monthly-other-income")}
                        value={t(financialInformation.prosjecanIznosOstalihPrihoda.vrijednost)}
                    />
                )}

                {financialInformation?.pozicijaZaposlenja?.vrijednost && (
                    <SummaryPageView
                        label={t("summary-page.financial-information.views.employment-position")}
                        value={t(financialInformation.pozicijaZaposlenja.vrijednost)}
                    />
                )}

                {Array.isArray(financialInformation?.svrheOtvaranjaRacuna) &&
                    financialInformation.svrheOtvaranjaRacuna.length > 0 && (
                        <SummaryPageView
                            label={t("summary-page.financial-information.views.account-opening-reason")}
                            value={financialInformation.svrheOtvaranjaRacuna.map(opt => t(opt.vrijednost)).join(', ')}
                        />
                    )}

                {Array.isArray(financialInformation?.izvoriOstalihPrihoda) &&
                    financialInformation.izvoriOstalihPrihoda.length > 0 && (
                        <SummaryPageView
                            label={t("summary-page.financial-information.views.other-sources-of-income")}
                            value={financialInformation.izvoriOstalihPrihoda.map(opt => t(opt.vrijednost)).join(', ')}
                        />
                    )}

                {Array.isArray(financialInformation?.vrsteTransakcijaNaRacunu) &&
                    financialInformation.vrsteTransakcijaNaRacunu.length > 0 && (
                        <SummaryPageView
                            label={t("summary-page.financial-information.views.account-transactions-type")}
                            value={financialInformation.vrsteTransakcijaNaRacunu.map(opt => t(opt.vrijednost)).join(', ')}
                        />
                    )}

                {Array.isArray(financialInformation?.drzavePrekogranicnoPlacanje) &&
                    financialInformation.drzavePrekogranicnoPlacanje.length > 0 && (
                        <SummaryPageView
                            label={t("summary-page.financial-information.views.cross-border-payment-states")}
                            value={financialInformation.drzavePrekogranicnoPlacanje.map(opt => getCountryById(opt.oznaka, lng).drzava).join(', ')}
                        />
                    )}

                {financialInformation?.prosjecneGotovinskeTransakcije?.vrijednost && (
                    <SummaryPageView
                        label={t("summary-page.financial-information.views.average-monthly-cash-income")}
                        value={t(financialInformation.prosjecneGotovinskeTransakcije.vrijednost)}
                    />
                )}

                {financialInformation?.ocekivaniGodisnjiPromet?.vrijednost && (
                    <SummaryPageView
                        label={t("summary-page.financial-information.views.expected-annual-funds-turnover")}
                        value={t(financialInformation.ocekivaniGodisnjiPromet.vrijednost)}
                    />
                )}

                {Array.isArray(financialInformation?.osnovePriljevaSredstava) &&
                    financialInformation.osnovePriljevaSredstava.length > 0 && (
                        <SummaryPageView
                            label={t("summary-page.financial-information.views.funds-inflow-basics")}
                            value={financialInformation.osnovePriljevaSredstava.map(opt => t(opt.vrijednost)).join(', ')}
                        />
                    )}
            </Card.Body>

            <Card.Body title={t("summary-page.crs-information.title")} className="!min-h-fit">
                <SummaryPageView
                    label={t("summary-page.crs-information.views.non-us-tax-payer")}
                    value={yesNo(taxResidency?.porezniObveznikDrzaveKojaNijeSAD)}
                />

                {taxResidency?.drzaveOporezivanja?.map((drzavaOporezivanja, index) => {
                        return (
                            <FormControl sx={{alignContent: 'center', width: '100%'}} key={index}>
                                <InputLabel sx={{backgroundColor: 'white', paddingInline: '6px'}} shrink>
                                    <p className="text-black">
                                      {t("summary-page.crs-information.views.taxation-country")}
                                    </p>
                                </InputLabel>
                                <Box sx={{
                                    paddingInline: '24px',
                                    paddingBlock: '12px',
                                    borderWidth: '1px',
                                    borderRadius: '24px',
                                    borderColor: '#9C9FA3',
                                    backgroundColor: '#f9fafb',
                                }}>
                                    <SummaryPageView
                                        label={t("summary-page.crs-information.views.state")}
                                        value={getCountryById(drzavaOporezivanja.country.oznaka, lng).drzava}
                                    />
                                    {drzavaOporezivanja.country.oznaka !== "HR" && (
                                        <SummaryPageView
                                            label={t("summary-page.crs-information.views.tax-number")}
                                            value={drzavaOporezivanja.taxNumber}
                                        />
                                    )}
                                </Box>
                            </FormControl>
                        )
                    }
                )}
            </Card.Body>

            <Card.Body title={t("summary-page.fatca-information.title")} bodyClassName="!space-y-0">
                {!hasUsCitizenship &&
                    <SummaryPageView
                        label={t("summary-page.fatca-information.views.dual-us-citizenship")}
                        value={yesNo(taxResidency?.imaDodatnoAmerickoDrzavljanstvo)}
                    />
                }

                <SummaryPageView
                    label={t("summary-page.fatca-information.views.green-card")}
                    value={yesNo(taxResidency?.zelenaKarta)}
                />

                <SummaryPageView
                    label={t("summary-page.fatca-information.views.american-tax-payer")}
                    value={yesNo(taxResidency?.porezniObveznikSAD)}
                />

                <SummaryPageView
                    label={t("summary-page.fatca-information.views.has-american-tin-number")}
                    value={yesNo(taxResidency?.posjedujePorezniBrojSAD)}
                />

                {!!taxResidency.porezniBrojSAD &&
                    <SummaryPageView
                        label={t("summary-page.fatca-information.views.american-tin-number")}
                        value={taxResidency.porezniBrojSAD}
                    />
                }
            </Card.Body>

            <Card.Body title={t("summary-page.political-exposure-information.title")} bodyClassName="!space-y-0">
                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.state-head")}
                    value={yesNo(politicalExposure?.celnikDrzava)}
                />

                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.legislature-member")}
                    value={yesNo(politicalExposure?.clanZakonodavnogTijela)}
                />

                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.governing-body-member")}
                    value={yesNo(politicalExposure?.clanUpravnogTijelaPolitika)}
                />

                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.constitutional-or-supreme-court-member")}
                    value={yesNo(politicalExposure?.sudVrhovniUstavni)}
                />

                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.auditors-court-member")}
                    value={yesNo(politicalExposure?.sudRevizorski)}
                />

                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.central-bank-council-member")}
                    value={yesNo(politicalExposure?.clanSavjetaSredisnjeBanke)}
                />

                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.diplomat")}
                    value={yesNo(politicalExposure?.diplomat)}
                />

                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.supervisory-board-member")}
                    value={yesNo(politicalExposure?.clanNadzornogOdbora)}
                />

                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.international-organizations-member")}
                    value={yesNo(politicalExposure?.clanMedjunarodnaOrganizacija)}
                />

                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.local-government-chief")}
                    value={yesNo(politicalExposure?.celnikLokalnaVlast)}
                />

                <p className="text-md pt-6 pb-1">
                    {t("summary-page.political-exposure-information.views.political-affiliate.direct-relation-info")}
                </p>

                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.political-affiliate.married-contact")}
                    value={yesNo(politicalExposure?.clanBracni)}
                />

                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.political-affiliate.descendant-contact")}
                    value={yesNo(politicalExposure?.clanPotomak)}
                />

                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.political-affiliate.parent-contact")}
                    value={yesNo(politicalExposure?.clanRoditelj)}
                />

                <p className="text-md pt-6 pb-1">
                    {t("summary-page.political-exposure-information.views.political-affiliate.side-relation-info")}
                </p>

                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.political-affiliate.business-co-owner-contact")}
                    value={yesNo(politicalExposure?.suradnikPoslovniZajedno)}
                />

                <SummaryPageView
                    label={t("summary-page.political-exposure-information.views.political-affiliate.business-contact")}
                    value={yesNo(politicalExposure?.suradnikPoslovni)}
                />

                {
                    politicalExposure?.politickeOsobe?.length > 0 && <p className="text-md pt-6 pb-1">
                        {t("summary-page.political-exposure-information.views.political-affiliate.contacts-list-info")}
                    </p>
                }
                {politicalExposure?.politickeOsobe?.length > 0 && (
                    <div className="space-y-5 pt-2">
                        {politicalExposure?.politickeOsobe?.map((politickaOsoba, index) => {
                                return (
                                    <FormControl sx={{alignContent: 'center', width: '100%',}} key={index}>
                                        <InputLabel sx={{backgroundColor: 'white', paddingInline: '6px'}} shrink>
                                            <p className="text-black">
                                                {t("summary-page.political-exposure-information.views.political-affiliate.contact-info")}
                                            </p>
                                        </InputLabel>
                                        <Box sx={{
                                            paddingInline: '24px',
                                            paddingBlock: '12px',
                                            borderWidth: '1px',
                                            borderRadius: '24px',
                                            borderColor: '#9C9FA3',
                                            backgroundColor: '#f9fafb',
                                        }}>
                                            <SummaryPageView
                                                label={t("summary-page.political-exposure-information.views.political-affiliate.contact-name-surname")}
                                                value={politickaOsoba.imePrezime}
                                            />
                                            <SummaryPageView
                                                label={t("summary-page.political-exposure-information.views.political-affiliate.contact-function")}
                                                value={politickaOsoba.funkcija}
                                            />
                                        </Box>
                                    </FormControl>
                                )
                            }
                        )}
                    </div>
                )}

                {(politicalExposure?.celnikDrzava ||
                    politicalExposure?.clanZakonodavnogTijela ||
                    politicalExposure?.clanUpravnogTijelaPolitika ||
                    politicalExposure?.sudVrhovniUstavni ||
                    politicalExposure?.sudRevizorski ||
                    politicalExposure?.clanSavjetaSredisnjeBanke ||
                    politicalExposure?.diplomat ||
                    politicalExposure?.clanNadzornogOdbora ||
                    politicalExposure?.clanMedjunarodnaOrganizacija ||
                    politicalExposure?.celnikLokalnaVlast ||
                    politicalExposure?.clanBracni ||
                    politicalExposure?.clanPotomak ||
                    politicalExposure?.clanRoditelj ||
                    politicalExposure?.suradnikPoslovniZajedno ||
                    politicalExposure?.suradnikPoslovni) && (
                    <>
                        <p className="text-md pt-6 pb-1">{t("summary-page.political-exposure-information.views.political-affiliate.fund-sources-info")}</p>

                        {politicalExposure?.izvorSredstava && (
                            <SummaryPageView
                                label={t("summary-page.political-exposure-information.views.political-affiliate.fund-sources")}
                                value={politicalExposure.izvorSredstava}
                            />
                        )}

                        {politicalExposure?.izvorSredstavaBanka && (
                            <SummaryPageView
                                label={t("summary-page.political-exposure-information.views.political-affiliate.fund-bank-sources")}
                                value={politicalExposure.izvorSredstavaBanka}
                            />
                        )}
                    </>
                )}
            </Card.Body>

            <Card.Body title={t("summary-page.marketing-consent-information.title")} bodyClassName="!space-y-0">
                <SummaryPageView
                    label={t("summary-page.marketing-consent-information.views.marketing-consent")}
                    value={yesNo(marketingConsent?.privolaMarketinskeInformacije ?? false)}
                />

                <SummaryPageView
                    label={t("summary-page.marketing-consent-information.views.personalized-offer")}
                    value={yesNo(marketingConsent?.privolaPersonaliziranePonude ?? false)}
                />

                {Array.isArray(marketingConsent?.kanaliDostave) &&
                    marketingConsent.kanaliDostave.length > 0 && (
                        <SummaryPageView
                            label={t("summary-page.marketing-consent-information.views.delivery-channels")}
                            value={marketingConsent.kanaliDostave.map(opt => t(opt.vrijednost)).join(', ')}
                        />
                    )}
            </Card.Body>

            <div className="text-center md:text-left">
                <Button
                    type="button"
                    onClick={handleReturn}
                    text={t("common.button-previous")}
                    variant="secondary"
                    disabled={false}
                />
                <Button
                    type="submit"
                    text={t("common.button-next")}
                    onClick={handleSubmit}
                    disabled={isLoading}
                />
            </div>

        </Card>
    );
};

export default SummaryPage;
